import {
  createEvoClient,
  createPayment,
  createPaymentSession,
  getPayment,
  getPaymentMethodClient,
  getPaymentMethods,
  paySession,
} from "@jti/evo-client";
import type {
  GetPaymentMethodsOptions,
  PaymentData,
  PgsApiContract,
  PgsOrderInput,
  PgsPaymentMethod,
  ProcessSecuredFieldsPaymentInput,
} from "@jti/fe-common";

import { getUser } from "../../../utils";

import { getPaymentDataFromEvoResponse, translatedPaymentMethodsEvo } from "./utils";

export class PgsApiEvo implements PgsApiContract {
  private readonly client;
  constructor(baseUrl: string, tenantToken: string, recaptchaId: string) {
    const user = getUser();
    this.client = createEvoClient({
      baseUrl,
      tenantToken,
      recaptchaId,
      ciamToken: user.ciam_token,
      throwOnError: true,
    });
  }

  async placeOrder(order: PgsOrderInput): Promise<string> {
    const { data } = await createPayment({
      client: this.client,
      body: {
        customer_uuid: order.customer.uuid,
        order_id: Number(order.orderId),
        tokenize: Boolean(order.tokenize_payment),
      },
    });

    return data?.data?.payment_token ?? "";
  }

  async getPaymentMethods(options: GetPaymentMethodsOptions = {}): Promise<{ data: PgsPaymentMethod[] }> {
    const query: { "filter[skus]"?: string } = {};

    if (options.skus?.length) {
      query["filter[skus]"] = options.skus.join(",");
    }

    const { data } = await getPaymentMethods({
      client: this.client,
      query,
    });

    return { data: translatedPaymentMethodsEvo(data?.data) };
  }

  async getClient(code: string): Promise<string> {
    const { data } = await getPaymentMethodClient({
      client: this.client,
      path: {
        code,
      },
    });
    return data?.data?.client ?? "";
  }

  async getOrderPaymentMethod(uuid: string): Promise<PaymentData> {
    const { data } = await getPayment({
      client: this.client,
      path: {
        uuid,
      },
    });

    return getPaymentDataFromEvoResponse(data);
  }

  async createPaymentSession(paymentMethod: string): Promise<{ session_id: string; merchant: string }> {
    const { data } = await createPaymentSession({
      client: this.client,
      body: {
        payment_method: paymentMethod,
      },
    });

    const sessionId: string = typeof data?.data?.session_id === "string" ? data.data.session_id : "";
    const merchantId: string = typeof data?.data?.merchant === "string" ? data.data.merchant : "";

    if (!sessionId) {
      throw new Error("Payment session initialization failed: missing session_id");
    }

    return {
      session_id: sessionId,
      merchant: merchantId,
    };
  }

  async processSecuredFieldsPayment({
    sessionId,
    orderId,
    consumerId,
    paymentMethod,
    tokenize,
  }: ProcessSecuredFieldsPaymentInput): Promise<PaymentData> {
    const { data } = await paySession({
      client: this.client,
      body: {
        session_id: sessionId,
        order_id: orderId,
        consumer_id: consumerId,
        payment_method: paymentMethod,
        tokenize,
      },
    });

    return getPaymentDataFromEvoResponse(data);
  }
}
